import { useEffect, useState } from 'react'
import type { MessageMedia } from '../api/types.js'

export interface AttachmentProps {
  media: MessageMedia
  /**
   * Скачивание вынесено параметром: компонент проверяется без клиента API
   * и без сети, а экран передаёт сюда `client.media`.
   */
  load: (id: string) => Promise<Blob>
}

/** Длительность звука или видео как её читают: 9 секунд — это 0:09. */
function длительность(seconds: number | null): string | null {
  if (seconds === null) return null
  const минуты = Math.floor(seconds / 60)
  const секунды = seconds % 60
  return `${минуты}:${String(секунды).padStart(2, '0')}`
}

/** Размер файла округлённо: точные байты человеку ничего не говорят. */
function размер(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`
}

/**
 * Вложение под сообщением переписки.
 *
 * Содержимое качается клиентом API и показывается по `blob:`-ссылке:
 * заголовок авторизации в `<img>` и `<audio>` не поставить, а второго
 * способа добраться до переписки — подписанной ссылки — заводить не
 * стали (FR-ADM-045).
 */
export function Attachment({ media, load }: AttachmentProps): React.JSX.Element {
  const [url, setUrl] = useState<string | null>(null)
  const [отказ, setОтказ] = useState(false)

  useEffect(() => {
    let живо = true
    let созданная: string | null = null

    void load(media.id).then(
      (blob) => {
        if (!живо) return
        созданная = URL.createObjectURL(blob)
        setUrl(созданная)
      },
      () => {
        if (живо) setОтказ(true)
      },
    )

    return () => {
      живо = false
      // Ссылка живёт, пока её не отозвали: без этого каждая открытая
      // переписка оставляет вложения в памяти вкладки до перезагрузки.
      if (созданная !== null) URL.revokeObjectURL(созданная)
    }
  }, [media.id, load])

  if (отказ) {
    return (
      <p className="attachment attachment--отказ">
        Вложение не удалось загрузить: {media.fileName}
      </p>
    )
  }

  if (url === null) {
    // Пока байтов нет, видно имя: молчащее место читается как поломка.
    return <p className="attachment attachment--ждёт">{media.fileName}</p>
  }

  if (media.kind === 'image' || media.kind === 'sticker') {
    return <img className="attachment attachment--изображение" src={url} alt={media.fileName} />
  }

  if (media.kind === 'audio') {
    const время = длительность(media.durationSeconds)
    return (
      <span className="attachment attachment--звук">
        <audio data-testid="attachment-audio" controls src={url} />
        {время !== null && <span className="attachment__время">{время}</span>}
      </span>
    )
  }

  if (media.kind === 'video') {
    return (
      <video data-testid="attachment-video" className="attachment attachment--видео" controls src={url} />
    )
  }

  /*
   * Всё остальное — файлом. Сюда же попадает вид, которого мы ещё не
   * знаем: появится новый у провайдера — он хотя бы сохранится, а не
   * исчезнет с экрана.
   */
  return (
    <span className="attachment attachment--файл">
      <span className="attachment__имя">{media.fileName}</span>
      <span className="attachment__размер">{размер(media.sizeBytes)}</span>
      <a className="btn btn--ghost btn--small" href={url} download={media.fileName}>
        Сохранить
      </a>
    </span>
  )
}
